import React from 'react';
import { useSoloTimetableStore, DAYS, PERIODS, getActivityColor, DOMAIN_COLORS } from '../../lib/stores/soloTimetableStore';
import { Icons } from '../../lib/Icons';

/**
 * SoloTimetableGrid - Visual timetable grid with transitions and breaks
 * Designed for parents/carers with a colorful, print-friendly layout
 */
const SoloTimetableGrid = ({ onEditSlot, readOnly = false }) => {
  const { grid, settings, getVisiblePeriods } = useSoloTimetableStore();
  const visiblePeriods = getVisiblePeriods();

  const handleSlotClick = (day, periodKey, periodType) => {
    if (readOnly) return;
    if (periodType === 'break') return; // Breaks are not editable
    if (onEditSlot) {
      onEditSlot(day, periodKey);
    }
  };

  const renderActivity = (activity, periodType) => {
    if (!activity) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-slate-500">
          <Icons.Plus className="w-5 h-5 mb-1 opacity-50" />
          <span className="text-xs opacity-75">Add activity</span>
        </div>
      );
    }

    const colors = getActivityColor(activity);
    const isTransition = periodType === 'transition' || activity.isTransition;

    if (isTransition) {
      return (
        <div className="flex items-center justify-center gap-2 h-full">
          <div className="w-6 h-6 rounded-full bg-amber-500/30 flex items-center justify-center">
            <Icons.MusicalNote className="w-3 h-3 text-amber-400" />
          </div>
          <span className="text-xs text-amber-300 font-medium truncate">{activity.title}</span>
        </div>
      );
    }

    return (
      <div className={`h-full p-2 rounded-lg border-l-4 ${colors.bg} ${colors.border}`}>
        <div className="flex items-start justify-between gap-1">
          <h4 className={`text-sm font-semibold ${colors.text} leading-tight line-clamp-2`}>
            {activity.title}
          </h4>
          {activity.matchScore > 0 && (
            <span className="flex-shrink-0 px-1.5 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
              {Math.round(activity.matchScore * 100)}%
            </span>
          )}
        </div>
        {activity.targetDomains && activity.targetDomains.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {activity.targetDomains.slice(0, 2).map((domain, idx) => {
              const domainColor = DOMAIN_COLORS[domain];
              return (
                <span
                  key={idx}
                  className={`px-1.5 py-0.5 text-xs rounded ${domainColor?.bg || 'bg-slate-700'} ${domainColor?.text || 'text-slate-400'}`}
                >
                  {domain.length > 10 ? domain.slice(0, 10) + '...' : domain}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderBreak = (period) => {
    const isLunch = period.key === 'lunch';
    return (
      <div className={`h-full flex items-center justify-center gap-2 ${isLunch ? 'bg-amber-500/10' : 'bg-yellow-500/10'} rounded-lg`}>
        <div className={`w-8 h-8 rounded-full ${isLunch ? 'bg-amber-500/30' : 'bg-yellow-500/30'} flex items-center justify-center`}>
          {isLunch ? (
            <Icons.Cake className="w-4 h-4 text-amber-400" />
          ) : (
            <Icons.Coffee className="w-4 h-4 text-yellow-400" />
          )}
        </div>
        <div className="text-center">
          <p className={`text-sm font-medium ${isLunch ? 'text-amber-300' : 'text-yellow-300'}`}>
            {period.label}
          </p>
          {settings.showTimes && (
            <p className="text-xs text-slate-500">{period.time}</p>
          )}
        </div>
      </div>
    );
  };

  const getPeriodHeight = (period) => {
    if (period.type === 'transition') return 'h-12';
    if (period.type === 'break') return period.key === 'lunch' ? 'h-16' : 'h-14';
    return 'h-24';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[800px]">
        {/* Header Row */}
        <thead>
          <tr>
            <th className="w-28 p-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider bg-slate-800/50 rounded-tl-lg">
              Time
            </th>
            {DAYS.map((day, idx) => (
              <th
                key={day}
                className={`p-2 text-center text-sm font-semibold text-white bg-slate-800/50 ${idx === DAYS.length - 1 ? 'rounded-tr-lg' : ''}`}
              >
                {day}
              </th>
            ))}
          </tr>
        </thead>

        {/* Body Rows */}
        <tbody>
          {visiblePeriods.map((period, periodIdx) => {
            const isLastRow = periodIdx === visiblePeriods.length - 1;
            const heightClass = getPeriodHeight(period);
            
            return (
              <tr key={period.key} className={period.type === 'transition' ? 'bg-slate-800/20' : ''}>
                {/* Time Column */}
                <td className={`p-2 border-t border-slate-700/50 ${isLastRow ? 'rounded-bl-lg' : ''}`}>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-slate-300">{period.label}</span>
                    {settings.showTimes && (
                      <span className="text-xs text-slate-500">{period.time}</span>
                    )}
                  </div>
                </td>

                {/* Day Columns */}
                {DAYS.map((day, dayIdx) => {
                  const slot = grid[day]?.[period.key];
                  const isLastCol = dayIdx === DAYS.length - 1;

                  return (
                    <td
                      key={`${day}-${period.key}`}
                      className={`p-1 border-t border-slate-700/50 ${isLastRow && isLastCol ? 'rounded-br-lg' : ''} ${heightClass}`}
                      onClick={() => handleSlotClick(day, period.key, period.type)}
                    >
                      <div
                        className={`h-full ${!readOnly && period.type !== 'break' ? 'cursor-pointer hover:bg-slate-700/30 rounded-lg transition-colors' : ''}`}
                      >
                        {period.type === 'break' ? (
                          renderBreak(period)
                        ) : (
                          renderActivity(slot?.activity, period.type)
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 justify-center">
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <span className="font-medium">Domains:</span>
        </div>
        {Object.entries(DOMAIN_COLORS).slice(0, 4).map(([domain, colors]) => (
          <div key={domain} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${colors.bg} ${colors.border} border`}></div>
            <span className="text-xs text-slate-400">{domain}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Compact version for preview/thumbnail
 */
export const SoloTimetableGridCompact = ({ grid }) => {
  const activityPeriods = PERIODS.filter(p => p.type === 'activity');

  return (
    <div className="grid grid-cols-5 gap-0.5 p-2 bg-slate-800/50 rounded-lg">
      {DAYS.map(day => (
        <div key={day} className="space-y-0.5">
          <div className="text-center text-xs font-medium text-slate-400 py-1">
            {day.slice(0, 3)}
          </div>
          {activityPeriods.map(period => {
            const slot = grid[day]?.[period.key];
            const hasActivity = slot?.activity !== null;
            const colors = hasActivity ? getActivityColor(slot.activity) : null;

            return (
              <div
                key={period.key}
                className={`h-4 rounded ${hasActivity ? colors?.bg : 'bg-slate-700/50'}`}
                title={slot?.activity?.title || 'Empty'}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

/**
 * Print-friendly version with light background
 */
export const SoloTimetableGridPrint = ({ grid, timetableName, settings }) => {
  const visiblePeriods = settings?.includeTransitions 
    ? PERIODS 
    : PERIODS.filter(p => p.type !== 'transition');

  const getPrintDomainColor = (domain) => {
    const colors = {
      'Communication': { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' },
      'SEMH': { bg: '#D1FAE5', border: '#10B981', text: '#065F46' },
      'Cognition': { bg: '#EDE9FE', border: '#8B5CF6', text: '#5B21B6' },
      'Sensory/Physical': { bg: '#FFEDD5', border: '#F97316', text: '#C2410C' },
      'Sensory': { bg: '#FFEDD5', border: '#F97316', text: '#C2410C' }
    };
    return colors[domain] || { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151' };
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', padding: '20px', backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '3px solid #6366F1', paddingBottom: '15px' }}>
        <h1 style={{ fontSize: '24px', color: '#1F2937', margin: '0 0 5px 0' }}>
          {timetableName || 'Weekly Timetable'}
        </h1>
        <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
          Generated on {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Timetable */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead>
          <tr style={{ backgroundColor: '#F3F4F6' }}>
            <th style={{ border: '1px solid #D1D5DB', padding: '8px', textAlign: 'left', fontWeight: '600', width: '100px' }}>
              Time
            </th>
            {DAYS.map(day => (
              <th key={day} style={{ border: '1px solid #D1D5DB', padding: '8px', textAlign: 'center', fontWeight: '600' }}>
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visiblePeriods.map(period => (
            <tr key={period.key}>
              <td style={{ 
                border: '1px solid #D1D5DB', 
                padding: '6px', 
                backgroundColor: '#F9FAFB',
                verticalAlign: 'top'
              }}>
                <div style={{ fontWeight: '600', fontSize: '10px' }}>{period.label}</div>
                <div style={{ fontSize: '9px', color: '#6B7280' }}>{period.time}</div>
              </td>
              {DAYS.map(day => {
                const slot = grid[day]?.[period.key];
                const activity = slot?.activity;

                if (period.type === 'break') {
                  return (
                    <td key={day} style={{ 
                      border: '1px solid #D1D5DB', 
                      padding: '8px', 
                      textAlign: 'center',
                      backgroundColor: period.key === 'lunch' ? '#FEF3C7' : '#FEF9C3'
                    }}>
                      <span style={{ fontWeight: '500', color: '#92400E' }}>{period.label}</span>
                    </td>
                  );
                }

                if (period.type === 'transition') {
                  return (
                    <td key={day} style={{ 
                      border: '1px solid #D1D5DB', 
                      padding: '4px', 
                      textAlign: 'center',
                      backgroundColor: '#FFFBEB'
                    }}>
                      <span style={{ fontSize: '9px', color: '#B45309' }}>
                        {activity?.title || 'Transition'}
                      </span>
                    </td>
                  );
                }

                if (!activity) {
                  return (
                    <td key={day} style={{ 
                      border: '1px solid #D1D5DB', 
                      padding: '6px', 
                      textAlign: 'center',
                      color: '#9CA3AF'
                    }}>
                      -
                    </td>
                  );
                }

                const domain = activity.targetDomains?.[0];
                const colors = getPrintDomainColor(domain);

                return (
                  <td key={day} style={{ 
                    border: '1px solid #D1D5DB', 
                    padding: '4px',
                    verticalAlign: 'top'
                  }}>
                    <div style={{
                      backgroundColor: colors.bg,
                      borderLeft: `3px solid ${colors.border}`,
                      padding: '6px',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontWeight: '600', color: colors.text, fontSize: '10px', marginBottom: '2px' }}>
                        {activity.title}
                      </div>
                      {activity.targetDomains && activity.targetDomains.length > 0 && (
                        <div style={{ fontSize: '8px', color: '#6B7280' }}>
                          {activity.targetDomains.slice(0, 2).join(', ')}
                        </div>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Legend */}
      <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
        {Object.entries({
          'Communication': '#3B82F6',
          'SEMH': '#10B981',
          'Cognition': '#8B5CF6',
          'Sensory/Physical': '#F97316'
        }).map(([domain, color]) => (
          <div key={domain} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: color, borderRadius: '2px' }}></div>
            <span style={{ fontSize: '10px', color: '#6B7280' }}>{domain}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SoloTimetableGrid;
